/**
 * Value-axis metadata derivation for chart series — currency detection,
 * tick formatting, and integer/negative-aware tick and minimum defaults.
 */

import {
  compactNumberFormatter,
  getCompactCurrencyFormatter,
  isCurrencyMeasure,
} from '../constants';
import type { ChartConfiguration } from '../types';

/**
 * Compute value-axis metadata for a set of series keys.
 *
 * @param chartData - Chart-ready rows (one value per series key, plus `x`/`xValues`)
 * @param seriesKeys - The data keys (measures) driving the value axis
 * @param columnTypes - Backend column type map, keyed by series key —
 *   REQUIRED: the currency check reads this before falling back to
 *   `isCurrencyMeasure`
 * @param config - Chart configuration (used for the y-axis scale)
 * @param currency - Currency code for the currency tick formatter
 * @returns Currency detection, an auto tick formatter, and integer/negative-aware
 *   tick and axis-minimum defaults
 */
export function computeValueAxisMeta(
  chartData: Record<string, unknown>[],
  seriesKeys: string[],
  columnTypes: Record<string, string>,
  config: ChartConfiguration,
  currency: string
): {
  hasCurrencyMeasure: boolean;
  autoTickFormatter: (value: number) => string;
  allIntegerValues: boolean;
  smallIntegerTicks: number[] | undefined;
  defaultValueAxisMin: number | 'auto';
} {
  const hasCurrencyMeasure =
    seriesKeys.length > 0 &&
    seriesKeys.every(key =>
      columnTypes[key] != null
        ? columnTypes[key] === 'currency'
        : isCurrencyMeasure(key)
    );

  // Auto-detect y-axis tick formatter based on measure types
  const autoTickFormatter = (value: number) =>
    hasCurrencyMeasure
      ? getCompactCurrencyFormatter(currency).format(value)
      : compactNumberFormatter.format(value);

  // Small all-integer domains (counts topping out at a handful) otherwise get
  // fractional gridlines (0.2, 0.4, …) — recharts' allowDecimals gives up
  // when dataMax < tickCount. Hand it explicit integer ticks instead.
  const numericValues = chartData.flatMap(item =>
    seriesKeys
      .map(key => Number(item[key as keyof typeof item]))
      .filter(v => !Number.isNaN(v))
  );
  const dataMax = numericValues.length > 0 ? Math.max(...numericValues) : 0;
  // Integer-only ticks are only correct when the data itself is all
  // integers — averages and other fractional measures keep decimal ticks.
  const allIntegerValues =
    numericValues.length > 0 && numericValues.every(Number.isInteger);
  const hasNegativeValues = numericValues.some(v => v < 0);
  // Explicit 0..max ticks only fit a nonnegative LINEAR axis: they omit
  // negative labels when the data dips below zero, and 0 is not a valid
  // logarithmic tick.
  const smallIntegerTicks =
    allIntegerValues &&
    !hasNegativeValues &&
    config.axes?.yAxisLeft.scale !== 'logarithmic' &&
    dataMax > 0 &&
    dataMax <= 4
      ? Array.from({ length: dataMax + 1 }, (_, i) => i)
      : undefined;
  // Zero baseline only for nonnegative linear data: a forced 0 minimum
  // clips negative bars, and a log scale cannot include 0 at all.
  const defaultValueAxisMin: number | 'auto' =
    hasNegativeValues || config.axes?.yAxisLeft.scale === 'logarithmic'
      ? 'auto'
      : 0;

  return {
    hasCurrencyMeasure,
    autoTickFormatter,
    allIntegerValues,
    smallIntegerTicks,
    defaultValueAxisMin,
  };
}
